// src/hooks/useEditorSocket.js
//
// Socket.IO realtime collaboration for the editor.
// Handles connection lifecycle, presence, and debounced content emit.

import { useRef, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { API_BASE } from '../utils/api';
import { getCleanHTML } from '../utils/editorSerialize';
import { COLLAB_BLOCK_SEL } from '../utils/collabHighlights';

/**
 * @param {object} params
 * @param {string|null}      params.docId            – doc._id to join
 * @param {React.RefObject}  params.editorRef        – ref to the contentEditable host
 * @param {Function}         params.setContent       – React state setter for content
 * @param {React.RefObject}  params.paginateRef      – ref to paginateAllPages function
 * @param {string|null}      params.currentUserId
 * @param {string}           params.currentUsername
 * @returns {{ onlineUsers: Array, emitDraftUpdate: (html: string) => void, storyElementsRev: number }}
 */
export default function useEditorSocket({
  docId,
  editorRef,
  setContent,
  paginateRef,
  currentUserId,
  currentUsername,
  onRemoteEdit,
}) {
  const socketRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  // Bumped whenever another collaborator changes the story elements board, so
  // the panel can re-read it. A counter, not the data: see the handler below.
  const [storyElementsRev, setStoryElementsRev] = useState(0);
  const [docVersion, setDocVersion] = useState(0);
  const docVersionRef = useRef(0);
  const emitTimerRef = useRef(null);
  // Latest onRemoteEdit callback, read via ref so the connect effect never
  // re-subscribes just because the parent passed a new inline function.
  const onRemoteEditRef = useRef(onRemoteEdit);

  // Keep refs in sync with state/props
  useEffect(() => {
    docVersionRef.current = docVersion;
  }, [docVersion]);
  useEffect(() => {
    onRemoteEditRef.current = onRemoteEdit;
  }, [onRemoteEdit]);

  // ---- connect / disconnect -------------------------------------------
  useEffect(() => {
    if (!docId) return;

    const socket = io(API_BASE, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      timeout: 8000,
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 500,
      reconnectionDelayMax: 4000,
    });
    socketRef.current = socket;

    // Helper: apply incoming HTML to the editor DOM
    const applyHtml = (html) => {
      if (html != null && editorRef.current) {
        editorRef.current.innerHTML = html;
        setContent(html);
        setTimeout(() => paginateRef.current?.(), 100);
      }
    };

    socket.on('connect', () => {
      setWsConnected(true);
      const initialHtml = getCleanHTML(editorRef.current);
      socket.emit('draft:join', {
        draftId: docId,
        content: initialHtml,
        userId: currentUserId ? String(currentUserId) : null,
        username: currentUsername,
      });
    });

    socket.on('connect_error', (err) => {
      console.error('[socket] connect_error:', err?.message || err);
    });

    socket.on('disconnect', (reason) => {
      setWsConnected(false);
      console.warn('[socket] disconnect:', reason);
    });

    socket.on('draft:joined', (payload) => {
      const { html, version } = payload || {};
      if (typeof version === 'number') setDocVersion(version);
      // Don't overwrite editor content loaded via HTTP fetch.
      // Only apply if the editor is genuinely empty (no visible text).
      const tmp = document.createElement('div');
      tmp.innerHTML = editorRef.current?.innerHTML || '';
      const editorHasContent = !!tmp.textContent.replace(/​/g, '').trim();
      if (html && !editorHasContent) {
        applyHtml(html);
      }
    });

    socket.on('draft:updated', (payload) => {
      const { html, version, userId, username } = payload || {};

      // Snapshot paragraph texts BEFORE applying so we can diff at block level.
      // Block-level diff is far more reliable than character diff: "the" typed
      // at end of a paragraph highlights that paragraph, not every "the" in the
      // document. We report each changed paragraph by its POSITION (index) in
      // the flat block list — COLLAB_BLOCK_SEL — so that when two people edit
      // the same paragraph, both are attributed to the same index and the
      // highlighter can blend their colors. Text is not used for matching.
      let prevBlocks = [];
      try {
        prevBlocks = Array.from(editorRef.current?.querySelectorAll(COLLAB_BLOCK_SEL) || [])
          .map(el => el.textContent);
      } catch {}

      applyHtml(html);
      if (typeof version === 'number') setDocVersion(version);

      // Find which paragraphs changed or were added and report each one's index.
      try {
        const uid = userId != null ? String(userId) : null;
        const uname = username || 'Collaborator';
        Array.from(editorRef.current?.querySelectorAll(COLLAB_BLOCK_SEL) || [])
          .forEach((el, i) => {
            const raw = el.textContent;
            const text = raw.trim();
            if (text.length < 3) return;
            if (prevBlocks[i] !== raw) {
              onRemoteEditRef.current?.({ userId: uid, username: uname, text, raw, paragraphIndex: i });
            }
          });
      } catch {
        /* highlighting is best-effort; never break content sync */
      }
    });

    socket.on('draft:ack', (payload) => {
      if (typeof payload?.version === 'number') setDocVersion(payload.version);
    });

    socket.on('draft:resync', (payload) => {
      const { html, version } = payload || {};
      applyHtml(html);
      if (typeof version === 'number') setDocVersion(version);
    });

    socket.on('draft:presence:update', (payload) => {
      setOnlineUsers(Array.isArray(payload?.users) ? payload.users : []);
    });

    // A collaborator changed the story elements board. The server sends a
    // signal rather than the change itself — the Story Elements panel holds
    // optimistic local state, and merging a delta into that is how a row ends
    // up flickering between two names — so this only says "re-read it".
    // Our own writes echo back here too; ignore them, or every keystroke that
    // autosaves would trigger a refetch that fights the field being typed in.
    socket.on('draft:story-elements:changed', (payload) => {
      if (payload?.userId && String(payload.userId) === String(currentUserId)) return;
      setStoryElementsRev((n) => n + 1);
    });

    return () => {
      if (emitTimerRef.current) {
        clearTimeout(emitTimerRef.current);
        emitTimerRef.current = null;
      }
      try {
        socket.emit('draft:leave', { draftId: docId });
      } catch {
        // ignore
      }
      socket.disconnect();
      socketRef.current = null;
      setWsConnected(false);
      setOnlineUsers([]);
    };
  }, [docId, currentUserId, currentUsername, editorRef, paginateRef, setContent]);

  // ---- debounced emit (150 ms) ----------------------------------------
  const emitDraftUpdate = useCallback(
    (html) => {
      // Use socket.connected for a fresh check (no stale closure on wsConnected)
      if (!socketRef.current?.connected) return;

      if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
      emitTimerRef.current = setTimeout(() => {
        try {
          socketRef.current?.emit('draft:update', {
            draftId: docId,
            version: docVersionRef.current,
            html,
          });
        } catch {}
      }, 150);
    },
    [docId],
  );

  return { onlineUsers, wsConnected, emitDraftUpdate, storyElementsRev };
}
